import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { STAGE_COLORS } from '../constants/brand';
import { useAuth } from '../contexts/AuthContext';

interface Job {
  id: string;
  pipeline_stage_name: string;
  job_type: string;
  leads: { homeowner_name: string; address: string; claim_number: string; insurance_company: string; lead_source: string; };
  users: { name: string; } | null;
  created_at: string;
  stage_entered_at?: string | null;
  commission_paid: boolean;
  square_footage: number;
  total_revenue: number;
  job_value?: number;
  net_profit: number;
  deposit_paid: boolean;
}

const money = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const JOB_TYPES = [
  { key: 'roofing',   label: '🏠 Roofing',    color: '#4A7EB5' },
  { key: 'insurance', label: '🛡️ Insurance',  color: '#06B6D4' },
  { key: 'asphalt',   label: '🛣️ Asphalt',    color: '#F59E0B' },
  { key: 'concrete',  label: '🪨 Concrete',    color: '#8B5CF6' },
];

const STAGE_TYPE_COLORS: Record<string, string> = {
  'Sold': '#10B981',
  'Deposit Received': '#10B981',
  'Scheduled': '#3B82F6',
  'In Progress': '#EF4444',
  'Job Complete': '#10B981',
  'Pending Payment': '#F59E0B',
  'Paid and Closed': '#10B981',
  'Paid': '#10B981',
  'Commission Paid': '#8B5CF6',
  'Closed': '#4A6580',
};

const getStageColor = (stage: string) => STAGE_TYPE_COLORS[stage] || STAGE_COLORS[stage] || '#4A7EB5';

const getDays = (d?: string | null) => {
  if (!d || !Number.isFinite(Date.parse(d))) return '—';
  const days = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));
  return days === 0 ? 'Today' : `${days}d`;
};

export default function PipelinePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSpecialist = user?.role === 'specialist' || user?.role === 'pm';
  const [catalog, setCatalog] = useState<{name: string; stage_type: string; description?: string}[]>([]);
  const stageList = (type: string) => catalog.filter(s => !s.description?.startsWith('hidden:') && s.stage_type === (['asphalt', 'concrete'].includes(type) ? 'ac' : type)).map(s => s.name);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('roofing');
  const [search, setSearch] = useState('');
  const [scheduleJob, setScheduleJob] = useState<Job | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('08:00');
  const [scheduling, setScheduling] = useState(false);

  // Drag and drop
  const [dragging, setDragging] = useState<string | null>(null);

  // Add Job modal
  const [showAddJob, setShowAddJob] = useState(false);
  const [addJobForm, setAddJobForm] = useState({ first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: 'DE', zip: '', job_type: 'roofing', pipeline_stage_name: 'New Lead', assigned_salesman_id: '', notes: '', insurance_company: '', claim_number: '' });
  const [salesmen, setSalesmen] = useState<any[]>([]);
  const [savingJob, setSavingJob] = useState(false);
  const [addJobError, setAddJobError] = useState('');

  const ajf = (k: string, v: string) => setAddJobForm(p => ({ ...p, [k]: v }));

  const openAddJob = async () => {
    setAddJobForm({ first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: 'DE', zip: '', job_type: activeType, pipeline_stage_name: stageList(activeType)[0] || 'New Lead', assigned_salesman_id: '', notes: '', insurance_company: '', claim_number: '' });
    setAddJobError('');
    if (!salesmen.length) {
      const res = await api.get('/users?role=salesman').catch(() => ({ data: [] }));
      setSalesmen(res.data || []);
    }
    setShowAddJob(true);
  };

  const handleAddJob = async () => {
    if (!addJobForm.first_name.trim()) return setAddJobError('First name is required');
    if (!addJobForm.last_name.trim()) return setAddJobError('Last name is required');
    if (!addJobForm.address.trim()) return setAddJobError('Address is required');
    const homeowner_name = `${addJobForm.first_name.trim()} ${addJobForm.last_name.trim()}`;
    setSavingJob(true); setAddJobError('');
    try {
      const res = await api.post('/jobs', { ...addJobForm, homeowner_name, homeowner_email: addJobForm.email.trim() || null });
      setJobs(prev => [{ ...res.data.job, leads: { homeowner_name, homeowner_email: addJobForm.email.trim() || null, address: addJobForm.address, claim_number: '', insurance_company: '', lead_source: 'manual' }, users: null, created_at: new Date().toISOString(), stage_entered_at: new Date().toISOString(), commission_paid: false, square_footage: 0, total_revenue: 0, net_profit: 0, deposit_paid: false, job_type: addJobForm.job_type, pipeline_stage_name: addJobForm.pipeline_stage_name }, ...prev]);
      setShowAddJob(false);
      navigate(`/pipeline/${res.data.job.id}`);
    } catch (e: any) {
      setAddJobError(e.response?.data?.error || 'Failed to create job');
    } finally { setSavingJob(false); }
  };

  const handleSchedule = async () => {
    if (!scheduleJob || !scheduleDate) return;
    setScheduling(true);
    try {
      await api.post(`/jobs/${scheduleJob.id}/schedule`, {
        scheduled_date: scheduleDate,
        scheduled_time: scheduleTime,
        deposit_collected: false,
      });
      setScheduleJob(null);
      setScheduleDate('');
      const { data } = await api.get('/jobs');
      setJobs(data);
      alert(`✅ Scheduled! Google Calendar invite created and homeowner notified.`);
    } catch (e: any) {
      alert('Failed to schedule: ' + (e?.response?.data?.error || e.message));
    } finally {
      setScheduling(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, jobId: string) => {
    setDragging(jobId);
    e.dataTransfer.setData('text/plain', jobId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStage: string) => {
    e.preventDefault();
    const jobId = e.dataTransfer.getData('text/plain');
    if (!jobId) return;

    if (newStage === 'Lost' || jobs.find(j => j.id === jobId)?.pipeline_stage_name === 'Lost') {
      setDragging(null); navigate(`/pipeline/${jobId}?action=disposition`); return;
    }

    // Optimistic update
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, pipeline_stage_name: newStage, stage_entered_at: new Date().toISOString() } : j));
    setDragging(null);

    try {
      await api.post(`/jobs/${jobId}/stage`, { stage_name: newStage });
    } catch (err) {
      console.error('Failed to update stage:', err);
      // Revert on error
      const { data } = await api.get('/jobs');
      setJobs(data);
    }
  };

  const handleDragEnd = () => {
    setDragging(null);
  };

  useEffect(() => {
    Promise.all([api.get('/jobs'), api.get('/pipeline/stages')]).then(([j, s]) => { setJobs(j.data); setCatalog(s.data); }).finally(() => setLoading(false));
  }, []);

  const currentType = JOB_TYPES.find(t => t.key === activeType)!;

  const stages = stageList(activeType);

  const filteredJobs = jobs.filter(j => {
    const jobType = j.job_type || 'roofing';
    const matchType = jobType === activeType;
    const matchSearch = !search ||
      j.leads?.homeowner_name?.toLowerCase().includes(search.toLowerCase()) ||
      j.leads?.address?.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const getJobsForStage = (stage: string) => filteredJobs.filter(j => j.pipeline_stage_name === stage);

  // When searching, only show stages that have matching jobs
  const displayedStages = search ? stages.filter(stage => getJobsForStage(stage).length > 0) : stages;

  const moveJob = async (jobId: string, stageName: string) => {
    if (stageName === 'Lost' || jobs.find(j => j.id === jobId)?.pipeline_stage_name === 'Lost') {navigate(`/pipeline/${jobId}?action=disposition`);return;}
    try {
      await api.post(`/jobs/${jobId}/stage`, { stage_name: stageName });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, pipeline_stage_name: stageName, stage_entered_at: new Date().toISOString() } : j));
    } catch (e) { console.error(e); }
  };

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>Pipeline</h1>
          {(user?.role === 'admin' || user?.role === 'specialist') && (
            <button onClick={openAddJob} style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              + Add Job
            </button>
          )}
        </div>

        {/* Job Type Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {JOB_TYPES.filter(t => !isSpecialist || t.key === 'roofing' || t.key === 'insurance').map(type => {
            const typeCount = jobs.filter(j => (j.job_type || 'roofing') === type.key).length;
            const isActive = activeType === type.key;
            return (
              <button
                key={type.key}
                onClick={() => setActiveType(type.key)}
                style={{
                  padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  background: isActive ? type.color : '#1A2B3C',
                  color: isActive ? '#fff' : typeCount > 0 ? '#CBD5E1' : '#8FA8C0',
                  border: `1px solid ${isActive ? type.color : typeCount > 0 ? type.color + '55' : '#2D4259'}`,
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {type.label}
                <span style={{
                  marginLeft: 8,
                  background: isActive ? 'rgba(255,255,255,0.25)' : typeCount > 0 ? type.color : '#2D4259',
                  color: '#fff',
                  borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 800,
                }}>
                  {typeCount}
                </span>
              </button>
            );
          })}
        </div>



        {/* Search */}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search by name or address..."
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 10,
            background: '#1A2B3C', border: '1px solid #2D4259',
            color: '#fff', fontSize: 14, boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Kanban Board */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', padding: '0 24px 24px' }}>
        <div style={{ display: 'flex', gap: 12, height: '100%', alignItems: 'flex-start' }}>
          {displayedStages.map(stage => {
            const stageJobs = getJobsForStage(stage);
            const color = getStageColor(stage);
            const displayName = stage;
            return (
              <div
                key={stage}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage)}
                style={{
                  width: 240, flexShrink: 0,
                  background: '#1A2B3C', borderRadius: 14,
                  border: '1px solid #2D4259',
                  borderTop: `3px solid ${color}`,
                  display: 'flex', flexDirection: 'column',
                  maxHeight: 'calc(100vh - 220px)',
                }}
              >
                {/* Column Header */}
                <div style={{ padding: '12px 14px', borderBottom: '1px solid #2D4259', flexShrink: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{displayName}</span>
                    <span style={{ background: color, color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                      {stageJobs.length}
                    </span>
                  </div>
                  <div style={{ color: '#10B981', fontSize: 14, fontWeight: 700, marginTop: 8 }}>
                    {money(stageJobs.reduce((sum, job) => sum + Math.round((job.job_value || 0) * 100), 0) / 100)}
                  </div>
                </div>

                {/* Cards */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
                  {stageJobs.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#4A6580', fontSize: 12, border: '1px dashed #2D4259', borderRadius: 8 }}>
                      Empty
                    </div>
                  ) : (
                    stageJobs.map(job => {
                      const days = getDays(job.stage_entered_at);
                      const isOld = parseInt(days) > 30;
                      return (
                        <div
                          key={job.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, job.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => navigate(`/pipeline/${job.id}`)}
                          style={{
                            background: '#243447', borderRadius: 10,
                            padding: 12, marginBottom: 8,
                            border: '1px solid #2D4259',
                            cursor: 'grab', transition: 'border-color 0.15s, opacity 0.15s',
                            opacity: dragging === job.id ? 0.5 : 1,
                          }}
                          onMouseEnter={e => (e.currentTarget.style.borderColor = color)}
                          onMouseLeave={e => (e.currentTarget.style.borderColor = '#2D4259')}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <div style={{ fontWeight: 700, color: '#fff', fontSize: 13, minWidth: 0, overflowWrap: 'anywhere' }}>{job.leads?.homeowner_name}</div>
                            {(job as any).job_number && <span style={{ fontSize: 10, color: '#4A7EB5', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{(job as any).job_number}</span>}
                          </div>
                          <div style={{ color: '#8FA8C0', fontSize: 11, marginBottom: 6 }}>{job.leads?.address}</div>
                          {job.leads?.claim_number && (
                            <div style={{ color: '#4A7EB5', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>🔖 {job.leads.claim_number}</div>
                          )}
                          {job.square_footage && (
                            <div style={{ color: '#8FA8C0', fontSize: 11, marginBottom: 4 }}>📐 {job.square_footage} SF</div>
                          )}
                          <div title="Job value" style={{ color: '#10B981', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{money(job.job_value || 0)}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                            <span style={{ color: '#4A6580', fontSize: 11 }}>
                              {(() => {
                                const name = job.users?.name || (job as any).leads?.users?.name;
                                return name ? `👤 ${name.split(' ')[0]}` : '⚠️';
                              })()}
                            </span>
                            <span title={job.stage_entered_at ? 'Days in current stage' : 'Stage entry date unavailable'} style={{ color: isOld ? '#EF4444' : '#4A6580', fontSize: 11, fontWeight: 600 }}>{days}</span>
                          </div>
                          {stage === 'Sold' && (
                            <button
                              onClick={e => { e.stopPropagation(); setScheduleJob(job); setScheduleDate(''); setScheduleTime('08:00'); }}
                              style={{ marginTop: 8, width: '100%', background: '#10B98122', color: '#10B981', border: '1px solid #10B98144', borderRadius: 6, padding: '5px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            >
                              📅 Schedule
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* Quick Schedule Modal */}
    {scheduleJob && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 28, width: 380, border: '1px solid #2D4259' }}>
          <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 4 }}>🗓️ Schedule Job</h2>
          <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 8 }}>{(scheduleJob as any).leads?.homeowner_name} · {(scheduleJob as any).leads?.address}</p>
          <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Start Date</label>
          <input
            type="date"
            value={scheduleDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={e => setScheduleDate(e.target.value)}
            style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }}
          />

          <label style={{ color: '#8FA8C0', fontSize: 12, display: 'block', marginBottom: 6 }}>Start Time</label>
          <input
            type="time"
            value={scheduleTime}
            onChange={e => setScheduleTime(e.target.value)}
            style={{ width: '100%', background: '#243447', border: '1px solid #2D4259', color: '#fff', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginBottom: 20, boxSizing: 'border-box' }}
          />

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setScheduleJob(null)}
              style={{ flex: 1, padding: '10px', background: '#243447', color: '#8FA8C0', border: '1px solid #2D4259', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              disabled={!scheduleDate || scheduling}
              style={{ flex: 2, padding: '10px', background: !scheduleDate || scheduling ? '#2D4259' : '#10B981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: !scheduleDate || scheduling ? 'default' : 'pointer' }}
            >
              {scheduling ? 'Scheduling...' : '✅ Confirm & Schedule'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* Add Job Modal */}
    {showAddJob && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#0F1923', border: '1px solid #1E3A5F', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ color: '#fff', margin: 0, fontSize: 18, fontWeight: 800 }}>+ Add Job</h2>
            <button onClick={() => setShowAddJob(false)} style={{ background: 'none', border: 'none', color: '#8FA8C0', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          {addJobError && <div style={{ background: '#EF444422', border: '1px solid #EF4444', borderRadius: 8, padding: '10px 14px', color: '#EF4444', fontSize: 13, marginBottom: 16 }}>{addJobError}</div>}

          {/* Job Type */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Job Type</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
              {JOB_TYPES.map(t => (
                <button key={t.key} onClick={() => { ajf('job_type', t.key); ajf('pipeline_stage_name', stageList(t.key)[0]); }}
                  style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: addJobForm.job_type === t.key ? t.color : '#1A2B3C', color: '#fff', border: `1px solid ${addJobForm.job_type === t.key ? t.color : '#2D4259'}` }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Homeowner Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>First Name *</label>
              <input value={addJobForm.first_name} onChange={e => ajf('first_name', e.target.value)} placeholder="John"
                style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Last Name *</label>
              <input value={addJobForm.last_name} onChange={e => ajf('last_name', e.target.value)} placeholder="Smith"
                style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
            </div>
          </div>

          {/* Phone */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Phone</label>
            <input value={addJobForm.phone} onChange={e => ajf('phone', e.target.value)} placeholder="(302) 555-0100"
              style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
          </div>

          {/* Email */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Email</label>
            <input value={addJobForm.email} onChange={e => ajf('email', e.target.value)} placeholder="homeowner@example.com" type="email" autoCapitalize="none" autoCorrect="off"
              style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
          </div>

          {/* Address */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Street Address *</label>
            <input value={addJobForm.address} onChange={e => ajf('address', e.target.value)} placeholder="123 Main St"
              style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>City</label>
              <input value={addJobForm.city} onChange={e => ajf('city', e.target.value)} placeholder="Newark"
                style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>State</label>
              <input value={addJobForm.state} onChange={e => ajf('state', e.target.value)} placeholder="DE"
                style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
            </div>
            <div>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>ZIP</label>
              <input value={addJobForm.zip} onChange={e => ajf('zip', e.target.value)} placeholder="19701"
                style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
            </div>
          </div>

          {/* Insurance fields (roofing + insurance jobs) */}
          {(addJobForm.job_type === 'roofing' || addJobForm.job_type === 'insurance') && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Insurance Company</label>
                <input value={addJobForm.insurance_company} onChange={e => ajf('insurance_company', e.target.value)} placeholder="State Farm, Allstate, etc."
                  style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Claim Number</label>
                <input value={addJobForm.claim_number} onChange={e => ajf('claim_number', e.target.value)} placeholder="Claim #"
                  style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' as const }} />
              </div>
            </>
          )}

          {/* Stage */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Pipeline Stage</label>
            <select value={addJobForm.pipeline_stage_name} onChange={e => ajf('pipeline_stage_name', e.target.value)}
              style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14 }}>
              {stageList(addJobForm.job_type).filter(s => s !== 'Lost').map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Assigned Salesman */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Assigned Salesman</label>
            <select value={addJobForm.assigned_salesman_id} onChange={e => ajf('assigned_salesman_id', e.target.value)}
              style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14 }}>
              <option value="">Unassigned</option>
              {salesmen.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Notes (optional)</label>
            <textarea value={addJobForm.notes} onChange={e => ajf('notes', e.target.value)} placeholder="Any additional info..."
              rows={3} style={{ width: '100%', background: '#1A2B3C', border: '1px solid #2D4259', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, resize: 'vertical' as const, boxSizing: 'border-box' as const }} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowAddJob(false)} style={{ flex: 1, background: '#1A2B3C', color: '#8FA8C0', border: '1px solid #2D4259', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleAddJob} disabled={savingJob} style={{ flex: 2, background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: savingJob ? 0.6 : 1 }}>
              {savingJob ? 'Creating...' : 'Create Job →'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
